// Canonical read-side record shapes + the minimal read seam for the Szzt service
// (szzt-spec.md §4, §9.2, §11.3).
//
// These are the §4 lexicon record VALUES as they appear in IPLD-JSON (cid-link fields as `{ $link }`,
// strong-ref `cid` fields as plain strings, optional fields omitted). They are the single source of
// truth shared by the write path (@szzt/publish), the reference verifier (@szzt/verify), and
// the browser-side gateway/service-worker verifier (@szzt/gateway). This package depends ONLY on
// @szzt/core, so read/resolve/recompute stay free of any network or write-path dependency.
//
// The shapes are read-lenient (optional fields the write path always sets are typed optional here);
// a consumer that always sets them narrows locally. Encoding correctness is proven byte-for-byte
// against a conforming PDS by the record-CID cross-check (test/record-cid.test.ts).

import type { MaslDocument } from "@szzt/core";

/** A cid-link as it appears in a record value; the dCBOR42 encoder emits it as a tag-42 CID. */
export interface CidLink {
  $link: string;
}

/** An atproto strong ref: an AT-URI plus the referenced record's atproto CID (com.atproto.repo.strongRef). */
export interface StrongRef {
  uri: string;
  /** Plain CID string (lexicon `format: cid`), NOT a `$link` cid-link. */
  cid: string;
}

/** `ing.dasl.masl` record value — the manifest/tile record (§4.3). Its own atproto CID is the manifest-record CID. */
export interface MaslRecordValue {
  $type: "ing.dasl.masl";
  /** dCBOR42 CID of the inline MASL = the site root CID = deployment `root`, as a PLAIN STRING per
   *  the upstream lexicon (`type: string, format: cid` — dasl.ing/tiles.html). Upstream consumers
   *  (e.g. the appmosphe.re tile index) read it with `typeof value.cid === "string"` and cannot
   *  read a `{$link}` cid-link, so the string form is load-bearing for interop. Records published
   *  before 2026-10 carry a `{ $link }` cid-link instead; readers accept both via `readCidField()`. */
  cid: string;
  /** The inline MASL document (§7.2). */
  tile: MaslDocument;
  /** cid-link to the previous version's manifest record CID, for lineage; omitted on first publish. */
  prev?: CidLink;
  createdAt: string;
}

/** `app.szzt.deployment` record value — an immutable version (§4.2). */
export interface DeploymentRecordValue {
  $type: "app.szzt.deployment";
  /** Root MASL CID (cid-link), equal to the paired manifest record's `cid`. */
  root: CidLink;
  entry?: string;
  /** Strong ref to the `ing.dasl.masl` record, pinning its atproto CID (§7.2). Required: it is the
   *  manifest record's only retrieval address on the PDS (the manifest lives as a record, not a
   *  root-CID-addressable blob), so `resolveTarget()` cannot reach the manifest without it. */
  tile: StrongRef;
  /** rkey of the owning `app.szzt.site` — a back-reference so a repo's append-only deployments can
   *  be grouped by site for version history (§16). Written by the publish path; omitted on records
   *  predating this field (they are unattributable and simply excluded from a site's history). */
  site?: string;
  createdAt: string;
}

/** `app.szzt.site` record value — stable identity and active pointer (§4.1). */
export interface SiteRecordValue {
  $type: "app.szzt.site";
  /** Strong ref to the active deployment (§4.1). */
  current: StrongRef;
  title?: string;
  slug?: string;
  hints?: string[];
  createdAt: string;
}

/** A record fetched from a repo (or gateway). `value` is the raw IPLD-JSON record body. */
export interface FetchedRecord {
  uri: string;
  /**
   * The record's atproto CID as ASSERTED by the source. A credible-exit verifier MUST NOT trust
   * this for integrity — it recomputes the CID from `value` and compares. Present because a source
   * usually returns it; may be empty when unknown.
   */
  cid: string;
  value: unknown;
  /**
   * Present only when the source PROVED this record against the repo's signed commit — today, only
   * `@szzt/verify`'s `atpReader`, which fetches a `com.atproto.sync.getRecord` proof CAR and
   * runs it through `verifyCarRecord`. Optional because the seam admits sources that cannot prove
   * anything (an in-memory test fake, a plain JSON fixture), NOT because proving is optional for the
   * network reader — that reader has no unverified path and never returns a record without this.
   *
   * Its ABSENCE is therefore not evidence of anything, and its PRESENCE is not a permission slip:
   * this is plain data a caller could write by hand. The unforgeable "this was proven" token is
   * `VerifiedRecord`'s unexported brand (`@szzt/verify` car.ts), which stays inside the verifier.
   * Read this as provenance to report or log, never as an integrity check to branch on.
   */
  proof?: {
    /** CID of the signed commit the proof hangs from. */
    commit: string;
    /** The record's CID as the signed MST filed it — equal to {@link FetchedRecord.cid}. */
    recordCid: string;
    /** The signed commit's revision. */
    rev: string;
  };
}

/**
 * Minimal read surface a resolver/verifier needs. An adapter over a read-only `@atproto/api` agent
 * (or a gateway) implements this; tests supply an in-memory fake. All CIDs are base32 CIDv1 strings.
 */
export interface RepoReader {
  /** Fetch a single record by its `at://did/collection/rkey` URI, or `null` if it does not exist. */
  getRecord(atUri: string): Promise<FetchedRecord | null>;
  /** Fetch a blob's exact bytes by the owning repo DID and the blob's `raw` CID (com.atproto.sync.getBlob). */
  getBlob(did: string, cid: string): Promise<Uint8Array>;
}

/** Thrown when a target AT-URI is malformed or names a collection the resolver cannot resolve. */
export class ResolveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ResolveError";
  }
}

/**
 * Thrown when the reader authoritatively reports a record as absent (`getRecord` returned `null`) — a
 * definitive "does not exist", distinct from a malformed/present record (plain {@link ResolveError}) or
 * an unreachable PDS (the reader throws). Subclasses `ResolveError` so existing catches still match;
 * callers that want to distinguish "not found" (→ 404) from "upstream broken" (→ 502) test for this.
 */
export class RecordNotFoundError extends ResolveError {
  constructor(message: string) {
    super(message);
    this.name = "RecordNotFoundError";
  }
}
